import { OperationContext } from "../../sync-engine/OperationContext";
import { Operation } from "../../sync-engine/operations/Operation";
import { generateRangeId, storeTrackedRange, getTrackedRangeMetadata, isRangeReleased } from "../TrackedRangeStore";
import type {
  QueryRangeParams,
  QueryRangeResult,
  RangeData,
  Op,
  Anchor,
  SearchOp,
  PickOp,
  EncloseOp,
  ExpandOp,
  NextOp,
  PrevOp,
  SpanToOp,
} from "@shared/lib/rpc/apis/query-range-types";

/**
 * Word SyncEngine Operation for querying ranges using a pipeline of operations.
 *
 * This implements a "query VM" that allows the AI agent to navigate
 * and query ranges within a Word document using composable operations.
 *
 * The pipeline processes ranges through a series of operations:
 * 1. Start with an anchor (document, selection, or tracked range)
 * 2. Apply operations to navigate ranges (Search, Pick, Enclose, Next/Prev, etc.)
 * 3. Return a single tracked range with extracted layer content
 *
 * All queries return a single range - use Pick to select from search results.
 */

/**
 * Default maximum results for Find/Search operations.
 */
const DEFAULT_MAX_RESULTS = 50;

export class QueryRangeOperation implements Operation<QueryRangeResult> {
  /**
   * Tracks whether results were truncated due to maxResults limit.
   */
  private resultsTruncated = false;

  /**
   * Tracks document boundary status for Next/Prev operations.
   */
  private atBoundary: "start" | "end" | undefined = undefined;

  constructor(private params: QueryRangeParams) {}

  async commit(context: OperationContext): Promise<QueryRangeResult> {
    // Step 1: Resolve anchor to initial range(s)
    let ranges = await this.resolveAnchor(this.params.anchor, context);

    if (ranges.length === 0) {
      throw new QueryError("NOT_FOUND", "Anchor did not match any ranges");
    }

    // Step 2: Apply operations in sequence
    for (let i = 0; i < this.params.ops.length; i++) {
      const op = this.params.ops[i];
      // Check if next op is Pick (for Search optimization)
      const nextOp = this.params.ops[i + 1];
      const nextIsPick = nextOp?.op === "Pick";
      try {
        ranges = await this.applyOp(op, ranges, context, nextIsPick);
      } catch (error) {
        if (error instanceof QueryError) {
          throw error;
        }
        throw new QueryError(
          "INVALID_PIPELINE",
          `Operation ${op.op} at index ${i} failed: ${error instanceof Error ? error.message : String(error)}`,
          i,
          `Check that the operation "${op.op}" is valid in the current context`,
        );
      }

      if (ranges.length === 0) {
        throw new QueryError("NOT_FOUND", `Pipeline produced no results after operation "${op.op}" at index ${i}`);
      }
    }

    // Step 3: Enforce single-range cardinality
    // All query results must be single ranges - use Pick to select from multiple results
    if (ranges.length > 1) {
      throw new QueryError(
        "CARDINALITY_ERROR",
        `Pipeline produced ${ranges.length} ranges. All queries must return a single range.`,
        undefined,
        'Add a Pick op (e.g., { "op": "Pick", "which": "first" }) to select one result.',
      );
    }

    // Step 4: Build result with tracked range
    const range = ranges[0];
    range.load("text");
    await context.sync();

    // Always get OOXML for layer extraction
    // Must use isolated sync because getOoxml() can throw GeneralException
    // for ranges that don't align with OOXML tag boundaries
    let ooxml: string;
    try {
      const ooxmlResult = range.getOoxml();
      await context.sync();
      ooxml = ooxmlResult.value;
    } catch (e) {
      // OOXML extraction failed - this is fatal since we need it for layer extraction
      throw new QueryError(
        "OOXML_EXTRACTION_FAILED",
        `Failed to extract OOXML from range: ${e instanceof Error ? e.message : String(e)}`,
        undefined,
        "The range may not align with OOXML boundaries. Try using Enclose to get a paragraph or other container.",
      );
    }

    if (!ooxml) {
      throw new QueryError(
        "OOXML_EXTRACTION_FAILED",
        "Range returned empty OOXML content",
        undefined,
        "The range may be empty or invalid. Try a different query.",
      );
    }

    // Generate ID and track the range
    // Mark as persistent so it survives after setSnippet edits
    // Store text metadata for re-finding the range in fresh contexts
    const rangeId = generateRangeId();
    range.track();
    storeTrackedRange(rangeId, range, true /* persistent */, {
      text: range.text,
    });

    // Build internal range data (ooxml is for layer extraction)
    const rangeData: RangeData = {
      trackedRangeId: rangeId,
      atBoundary: this.atBoundary,
      ooxml,
    };

    // Return result - the client handler will extract layer content and build final result
    // We return internal format with ooxml; client strips it after layer extraction
    return {
      trackedRangeId: rangeData.trackedRangeId,
      layer: "", // Placeholder - client fills this via server-side layer extraction
      atBoundary: rangeData.atBoundary,
      truncated: this.resultsTruncated || undefined,
      ooxml: rangeData.ooxml,
    };
  }

  /**
   * Resolve an anchor to initial range(s).
   */
  private async resolveAnchor(anchor: Anchor, context: OperationContext): Promise<Word.Range[]> {
    switch (anchor.kind) {
      case "document": {
        const body = context.wordContext.document.body;
        const range = body.getRange("Whole");
        range.load("text");
        await context.sync();
        return [range];
      }

      case "selection": {
        const selection = context.wordContext.document.getSelection();
        selection.load("text,paragraphs");
        await context.sync();

        // Expand selection to full paragraphs for reliable OOXML extraction.
        // A partial paragraph selection (or empty cursor) can cause GeneralException
        // when extracting OOXML because it may not align with XML tag boundaries.
        const paragraphs = selection.paragraphs;
        paragraphs.load("text");
        await context.sync();

        // Even an empty cursor position (zero-width selection) should have at least
        // one paragraph. If we somehow have none, try to get the first paragraph
        // from the selection's parent body as a fallback.
        if (paragraphs.items.length === 0) {
          const bodyParagraphs = context.wordContext.document.body.paragraphs;
          const firstParagraph = bodyParagraphs.getFirstOrNullObject();
          firstParagraph.load("isNullObject");
          await context.sync();

          if (firstParagraph.isNullObject) {
            // Document has no paragraphs at all - return selection as-is
            return [selection];
          }

          const fallbackRange = firstParagraph.getRange("Whole");
          fallbackRange.load("text");
          await context.sync();
          return [fallbackRange];
        }

        // Get range spanning all paragraphs (from start of first to end of last)
        const first = paragraphs.items[0].getRange("Start");
        const last = paragraphs.items[paragraphs.items.length - 1].getRange("End");
        const expandedRange = first.expandTo(last);
        expandedRange.load("text");
        await context.sync();

        return [expandedRange];
      }

      case "range": {
        // Get the stored metadata for this range (text content)
        const metadata = getTrackedRangeMetadata(anchor.rangeId);
        if (!metadata) {
          // Check if this range was previously valid but released
          if (isRangeReleased(anchor.rangeId)) {
            throw new QueryError(
              "RANGE_RELEASED",
              `Tracked range "${anchor.rangeId}" was released due to memory management (LRU eviction or expiration).`,
              undefined,
              "Run the query again to get a fresh tracked range ID.",
            );
          }
          throw new QueryError(
            "NOT_FOUND",
            `Tracked range "${anchor.rangeId}" not found. It may have expired or the document was reloaded.`,
          );
        }

        // Re-find the range by searching for the stored text in the fresh context.
        // This is necessary because Word.Range objects from previous contexts
        // can't be reliably used across different Word.run() calls.
        const body = context.wordContext.document.body;
        const searchResults = body.search(metadata.text, {
          matchCase: true,
          matchWholeWord: false,
        });
        searchResults.load("items");
        await context.sync();

        if (searchResults.items.length === 0) {
          throw new QueryError(
            "NOT_FOUND",
            `Could not re-find tracked range "${anchor.rangeId}". The document may have changed.`,
            undefined,
            "Re-query the document to get a fresh tracked range.",
          );
        }

        // Return the first match
        // TODO: If disambiguation is needed, we can add context matching here
        const foundRange = searchResults.items[0];
        foundRange.load();
        await context.sync();
        return [foundRange];
      }

      default: {
        const _exhaustive: never = anchor;
        throw new QueryError("INVALID_PIPELINE", `Unknown anchor kind: ${JSON.stringify(_exhaustive)}`);
      }
    }
  }

  /**
   * Apply a single operation to transform ranges.
   * @param nextIsPick - True if the next operation in the pipeline is Pick (for Search optimization)
   */
  private async applyOp(
    op: Op,
    ranges: Word.Range[],
    context: OperationContext,
    nextIsPick?: boolean,
  ): Promise<Word.Range[]> {
    switch (op.op) {
      case "Search":
        return this.applySearchOp(op as SearchOp, ranges, context, nextIsPick);

      case "Pick":
        return this.applyPickOp(op as PickOp, ranges);

      case "Enclose":
        return this.applyEncloseOp(op as EncloseOp, ranges, context);

      case "Expand":
        return this.applyExpandOp(op as ExpandOp, ranges, context);

      case "Next":
        return this.applyNextOp(op as NextOp, ranges, context);

      case "Prev":
        return this.applyPrevOp(op as PrevOp, ranges, context);

      case "SpanTo":
        return this.applySpanToOp(op as SpanToOp, ranges, context);

      case "Pages":
      case "FilterPages":
      case "PageRange":
        throw new QueryError(
          "REQUIREMENT_NOT_MET",
          `Operation "${op.op}" requires WordApiDesktop 1.2 which is not currently supported`,
          undefined,
          "Page operations are only available on desktop Word",
        );

      case "List":
        throw new QueryError(
          "REQUIREMENT_NOT_MET",
          "List operations are not yet implemented",
          undefined,
          "Use Find/Enclose operations to navigate list content",
        );

      case "ClipTo":
        throw new QueryError("REQUIREMENT_NOT_MET", "ClipTo operation is not yet implemented");

      case "Between":
        throw new QueryError("REQUIREMENT_NOT_MET", "Between operation is not yet implemented");

      default: {
        const _exhaustive: never = op;
        throw new QueryError("INVALID_PIPELINE", `Unknown operation: ${JSON.stringify(_exhaustive)}`);
      }
    }
  }

  /**
   * Search operation: search for text within each range.
   *
   * @param op - Search operation with text and match options
   * @param op.text - Text to search for
   * @param op.matchCase - Match case exactly (default: false)
   * @param op.matchWholeWord - Match whole words only (default: false)
   * @param op.matchWildcards - Enable Word wildcards (default: false)
   * @param op.maxResults - Max results to return (default: 1 if no Pick follows, else 50)
   * @param ranges - Source ranges to search within
   * @param context - Operation context for syncing
   * @param nextIsPick - Optimization: true if next op is Pick (enables multi-result)
   *
   * @example
   * // Find first occurrence of "WHEREAS"
   * { op: "Search", text: "WHEREAS" }
   *
   * @example
   * // Find up to 10 occurrences for iteration
   * { op: "Search", text: "Article", maxResults: 10 }
   */
  private async applySearchOp(
    op: SearchOp,
    ranges: Word.Range[],
    context: OperationContext,
    nextIsPick?: boolean,
  ): Promise<Word.Range[]> {
    const results: Word.Range[] = [];
    // If no Pick follows and no explicit maxResults, return only first match
    const implicitLimit = !nextIsPick && op.maxResults === undefined;
    const maxResults = implicitLimit ? 1 : op.maxResults ?? DEFAULT_MAX_RESULTS;

    for (const range of ranges) {
      const searchResults = range.search(op.text, {
        matchCase: op.matchCase ?? false,
        matchWholeWord: op.matchWholeWord ?? false,
        matchWildcards: op.matchWildcards ?? false,
      });
      searchResults.load("text"); // Load a real property to make items available
      await context.sync();

      for (const item of searchResults.items) {
        if (results.length >= maxResults) {
          this.resultsTruncated = true;
          break;
        }
        item.load();
        results.push(item);
      }

      if (this.resultsTruncated) {
        break;
      }
    }

    // Batch sync all loaded items at once
    await context.sync();
    return results;
  }

  /**
   * Pick operation: select a single range.
   */
  private applyPickOp(op: PickOp, ranges: Word.Range[]): Word.Range[] {
    if (ranges.length === 0) {
      return [];
    }

    let index: number;
    if (op.which === "first") {
      index = 0;
    } else if (op.which === "last") {
      index = ranges.length - 1;
    } else {
      index = op.which;
    }

    if (index < 0 || index >= ranges.length) {
      throw new QueryError("NOT_FOUND", `Pick index ${index} out of bounds (have ${ranges.length} ranges)`);
    }

    return [ranges[index]];
  }

  /**
   * Enclose operation: expand to enclosing container.
   */
  private async applyEncloseOp(op: EncloseOp, ranges: Word.Range[], context: OperationContext): Promise<Word.Range[]> {
    const results: Word.Range[] = [];

    for (const range of ranges) {
      let enclosingRange: Word.Range | null = null;

      switch (op.which) {
        case "paragraph": {
          const paragraphs = range.paragraphs;
          paragraphs.load("text"); // Load a real property, not "items"
          await context.sync();
          if (paragraphs.items.length > 0) {
            // Get range spanning all paragraphs
            const first = paragraphs.items[0].getRange("Start");
            const last = paragraphs.items[paragraphs.items.length - 1].getRange("End");
            enclosingRange = first.expandTo(last);
            enclosingRange.load();
          }
          break;
        }

        case "table": {
          // First try parentTableCellOrNullObject to find the parent table (when range is inside a cell)
          // Using OrNullObject pattern instead of try/catch for cleaner control flow
          const cellForTable = range.parentTableCellOrNullObject;
          cellForTable.load("isNullObject, parentTable");
          await context.sync();

          if (!cellForTable.isNullObject) {
            // Range is inside a table cell, get the parent table
            const table = cellForTable.parentTable;
            table.load();
            await context.sync();
            enclosingRange = table.getRange("Whole");
            enclosingRange.load();
          } else {
            // Range is not in a table cell, try tables within the range.
            // If multiple tables exist in the range, only the first table is selected.
            const tables = range.tables;
            tables.load("rowCount"); // Load a real property to make items available
            await context.sync();
            if (tables.items.length > 0) {
              enclosingRange = tables.items[0].getRange("Whole");
              enclosingRange.load();
            }
          }
          break;
        }

        case "row": {
          // Get parent table cell, then its row
          // Note: TableRow doesn't have getRange(), so we select it and get the selection.
          // This has a side effect of moving the user's cursor.
          const cellForRow = range.parentTableCellOrNullObject;
          cellForRow.load("isNullObject, parentRow");
          await context.sync();

          if (!cellForRow.isNullObject) {
            const row = cellForRow.parentRow;
            row.select(); // Select the entire row in Word
            await context.sync();
            enclosingRange = context.wordContext.document.getSelection();
            enclosingRange.load();
          }
          break;
        }
      }

      if (enclosingRange) {
        await context.sync();
        results.push(enclosingRange);
      }
    }

    // If no enclosing containers were found, throw a specific error
    if (results.length === 0 && ranges.length > 0) {
      const containerType = op.which;
      const containerTypeMap: Record<string, string> = {
        table: "a table",
        row: "a table row",
        paragraph: "a paragraph",
      };
      const containerName = containerTypeMap[containerType] || containerType;

      throw new QueryError(
        "NO_ENCLOSING_CONTAINER",
        `The selected text is not inside ${containerName}. The text may be in the document body outside of any ${containerType}.`,
        undefined,
        containerType === "table" || containerType === "row"
          ? "Search for text that is inside a table, or use a different query approach."
          : undefined,
      );
    }

    return results;
  }

  /**
   * Expand operation: expand range boundaries by paragraphs.
   *
   * IMPORTANT: This operation ONLY works with paragraphs. It uses
   * Paragraph.getNext()/getPrevious() for reliable navigation.
   *
   * @param op - Expand operation configuration
   * @param op.direction - "start" (backward), "end" (forward), or "both"
   * @param op.count - Number of paragraphs to expand by
   * @param ranges - Ranges to expand
   * @param context - Operation context for syncing
   *
   * @example
   * // Expand 3 paragraphs before and after
   * { op: "Expand", direction: "both", count: 3 }
   */
  private async applyExpandOp(op: ExpandOp, ranges: Word.Range[], context: OperationContext): Promise<Word.Range[]> {
    const results: Word.Range[] = [];

    for (const range of ranges) {
      // Rehydrate the range first (needed for tracked ranges from previous contexts)
      range.load("text");
      await context.sync();

      // Get the paragraphs within this range
      const rangeParagraphs = range.paragraphs;
      rangeParagraphs.load();
      await context.sync();

      if (rangeParagraphs.items.length === 0) {
        // Range doesn't contain any paragraphs, return as-is
        results.push(range);
        continue;
      }

      // Get the first and last paragraphs of the current range
      let startPara = rangeParagraphs.items[0];
      let endPara = rangeParagraphs.items[rangeParagraphs.items.length - 1];

      // Expand backward from start paragraph
      if (op.direction === "start" || op.direction === "both") {
        for (let i = 0; i < op.count; i++) {
          const prevPara = startPara.getPreviousOrNullObject();
          prevPara.load("isNullObject");
          await context.sync();
          if (prevPara.isNullObject) {
            // Reached start of document
            break;
          }
          startPara = prevPara;
        }
      }

      // Expand forward from end paragraph
      if (op.direction === "end" || op.direction === "both") {
        for (let i = 0; i < op.count; i++) {
          const nextPara = endPara.getNextOrNullObject();
          nextPara.load("isNullObject");
          await context.sync();
          if (nextPara.isNullObject) {
            // Reached end of document
            break;
          }
          endPara = nextPara;
        }
      }

      // Create range spanning from start to end paragraph
      const startRange = startPara.getRange("Start");
      const endRange = endPara.getRange("End");
      const expanded = startRange.expandTo(endRange);
      expanded.load();

      await context.sync();
      results.push(expanded);
    }

    return results;
  }

  /**
   * Next operation: move to the next paragraph(s) after the current range.
   *
   * Uses Paragraph.getNext() for reliable navigation. Sets atBoundary="end"
   * if the end of the document is reached.
   */
  private async applyNextOp(op: NextOp, ranges: Word.Range[], context: OperationContext): Promise<Word.Range[]> {
    const count = op.count ?? 1;

    if (ranges.length === 0) {
      return [];
    }

    // Take the last range as our reference point
    const range = ranges[ranges.length - 1];

    // Rehydrate the range first (needed for tracked ranges from previous contexts)
    range.load("text");
    await context.sync();

    // Get the paragraphs within this range
    const rangeParagraphs = range.paragraphs;
    rangeParagraphs.load();
    await context.sync();

    if (rangeParagraphs.items.length === 0) {
      // No paragraphs in range, return as-is
      this.atBoundary = "end";
      return ranges;
    }

    // Start from the last paragraph of the range
    let currentPara = rangeParagraphs.items[rangeParagraphs.items.length - 1];

    // Navigate forward by count paragraphs
    for (let i = 0; i < count; i++) {
      const nextPara = currentPara.getNextOrNullObject();
      nextPara.load("isNullObject");
      await context.sync();

      if (nextPara.isNullObject) {
        // Reached end of document
        this.atBoundary = "end";
        // Return the last valid paragraph
        const resultRange = currentPara.getRange("Whole");
        resultRange.load();
        await context.sync();
        return [resultRange];
      }
      currentPara = nextPara;
    }

    // Return the target paragraph
    const resultRange = currentPara.getRange("Whole");
    resultRange.load();
    await context.sync();
    return [resultRange];
  }

  /**
   * Prev operation: move to the previous paragraph(s) before the current range.
   *
   * Uses Paragraph.getPrevious() for reliable navigation. Sets atBoundary="start"
   * if the beginning of the document is reached.
   */
  private async applyPrevOp(op: PrevOp, ranges: Word.Range[], context: OperationContext): Promise<Word.Range[]> {
    const count = op.count ?? 1;

    if (ranges.length === 0) {
      return [];
    }

    // Take the first range as our reference point
    const range = ranges[0];

    // Rehydrate the range first (needed for tracked ranges from previous contexts)
    range.load("text");
    await context.sync();

    // Get the paragraphs within this range
    const rangeParagraphs = range.paragraphs;
    rangeParagraphs.load();
    await context.sync();

    if (rangeParagraphs.items.length === 0) {
      // No paragraphs in range, return as-is
      this.atBoundary = "start";
      return ranges;
    }

    // Start from the first paragraph of the range
    let currentPara = rangeParagraphs.items[0];

    // Navigate backward by count paragraphs
    for (let i = 0; i < count; i++) {
      const prevPara = currentPara.getPreviousOrNullObject();
      prevPara.load("isNullObject");
      await context.sync();

      if (prevPara.isNullObject) {
        // Reached start of document
        this.atBoundary = "start";
        // Return the first valid paragraph
        const resultRange = currentPara.getRange("Whole");
        resultRange.load();
        await context.sync();
        return [resultRange];
      }
      currentPara = prevPara;
    }

    // Return the target paragraph
    const resultRange = currentPara.getRange("Whole");
    resultRange.load();
    await context.sync();
    return [resultRange];
  }

  /**
   * SpanTo operation: Create a range spanning between two points.
   * Uses Word.Range.expandTo() to create a contiguous range covering both endpoints.
   *
   * Three modes:
   * - CurrentToTracked: Span from the current pipeline range to a tracked range (rangeId)
   * - BetweenTracked: Span between two tracked ranges (startRangeId + endRangeId)
   * - BetweenTextMatches: Search for startText and endText, span between matches
   */
  private async applySpanToOp(op: SpanToOp, ranges: Word.Range[], context: OperationContext): Promise<Word.Range[]> {
    // Determine which mode we're in
    const mode = this.determineSpanToMode(op);

    // Upfront validation for modes that require pipeline ranges
    if ((mode === "CurrentToTracked" || mode === "BetweenTextMatches") && ranges.length === 0) {
      const hint =
        mode === "BetweenTextMatches"
          ? "Use anchor: { kind: 'document' } to search the whole document"
          : "Ensure previous operations produce at least one range";
      throw new QueryError("INVALID_PIPELINE", "SpanTo requires a source range to span from", undefined, hint);
    }

    // Execute mode-specific logic
    switch (mode) {
      case "BetweenTracked":
        return this.spanBetweenTrackedRanges(op.startRangeId!, op.endRangeId!, context);

      case "BetweenTextMatches":
        return this.spanBetweenTextMatches(op.startText!, op.endText!, ranges[0], context);

      case "CurrentToTracked":
        return this.spanCurrentToTracked(op.rangeId!, ranges, context);
    }
  }

  /**
   * Determine which SpanTo mode based on the provided parameters.
   */
  private determineSpanToMode(op: SpanToOp): "CurrentToTracked" | "BetweenTracked" | "BetweenTextMatches" {
    if (op.startRangeId && op.endRangeId) {
      return "BetweenTracked";
    }
    if (op.startText && op.endText) {
      return "BetweenTextMatches";
    }
    if (op.rangeId) {
      return "CurrentToTracked";
    }

    throw new QueryError(
      "INVALID_PIPELINE",
      "SpanTo requires rangeId, startRangeId+endRangeId, OR startText+endText",
      undefined,
      'Use { op: "SpanTo", rangeId: "<id>" }, { op: "SpanTo", startRangeId: "<id1>", endRangeId: "<id2>" }, or { op: "SpanTo", startText: "...", endText: "..." }',
    );
  }

  /**
   * BetweenTracked mode: Span between two tracked ranges by their IDs.
   * Re-finds both ranges by text search to ensure they're in the same context.
   */
  private async spanBetweenTrackedRanges(
    startRangeId: string,
    endRangeId: string,
    context: OperationContext,
  ): Promise<Word.Range[]> {
    // Validate and get metadata for both tracked ranges
    const startMetadata = this.getTrackedRangeMetadataOrThrow(startRangeId, "Start range");
    const endMetadata = this.getTrackedRangeMetadataOrThrow(endRangeId, "End range");

    // Search for both texts in the document body.
    // This ensures both ranges are in the same context for expandTo().
    const body = context.wordContext.document.body;
    const startResults = body.search(startMetadata.text, { matchCase: true });
    const endResults = body.search(endMetadata.text, { matchCase: true });
    startResults.load("items");
    endResults.load("items");
    await context.sync();

    if (startResults.items.length === 0) {
      throw new QueryError(
        "NOT_FOUND",
        `Could not re-find start range "${startRangeId}". The document may have changed.`,
        undefined,
        "Re-query the document to get fresh tracked ranges.",
      );
    }

    if (endResults.items.length === 0) {
      throw new QueryError(
        "NOT_FOUND",
        `Could not re-find end range "${endRangeId}". The document may have changed.`,
        undefined,
        "Re-query the document to get fresh tracked ranges.",
      );
    }

    // Create spanning range from first match of each
    const spannedRange = startResults.items[0].expandTo(endResults.items[0]);
    spannedRange.load();
    await context.sync();

    return [spannedRange];
  }

  /**
   * BetweenTextMatches mode: Search for start and end text, span between paragraph boundaries.
   */
  private async spanBetweenTextMatches(
    startText: string,
    endText: string,
    searchScope: Word.Range,
    context: OperationContext,
  ): Promise<Word.Range[]> {
    // Search for both texts within the scope
    const startResults = searchScope.search(startText, { matchCase: false });
    const endResults = searchScope.search(endText, { matchCase: false });
    startResults.load("items");
    endResults.load("items");
    await context.sync();

    if (startResults.items.length === 0) {
      throw new QueryError("NOT_FOUND", `Start text "${startText}" not found in the search scope`);
    }

    if (endResults.items.length === 0) {
      throw new QueryError("NOT_FOUND", `End text "${endText}" not found in the search scope`);
    }

    // Enclose each match to its containing paragraph for clean boundaries
    const startParas = startResults.items[0].paragraphs;
    const endParas = endResults.items[0].paragraphs;
    startParas.load("items");
    endParas.load("items");
    await context.sync();

    // Span from first paragraph of start match to last paragraph of end match
    const startRange = startParas.items[0].getRange("Start");
    const endRange = endParas.items[endParas.items.length - 1].getRange("End");
    const spannedRange = startRange.expandTo(endRange);
    spannedRange.load();
    await context.sync();

    return [spannedRange];
  }

  /**
   * CurrentToTracked mode: Span from current pipeline range(s) to a tracked range.
   */
  private async spanCurrentToTracked(
    targetRangeId: string,
    sourceRanges: Word.Range[],
    context: OperationContext,
  ): Promise<Word.Range[]> {
    // Validate and get metadata for the target tracked range
    const targetMetadata = this.getTrackedRangeMetadataOrThrow(targetRangeId, "Tracked range");

    // Re-find the target range by text search in the current context
    const body = context.wordContext.document.body;
    const targetResults = body.search(targetMetadata.text, { matchCase: true });
    targetResults.load("items");
    await context.sync();

    if (targetResults.items.length === 0) {
      throw new QueryError(
        "NOT_FOUND",
        `Could not re-find tracked range "${targetRangeId}". The document may have changed.`,
        undefined,
        "Re-query the document to get a fresh tracked range.",
      );
    }

    const targetRange = targetResults.items[0];
    const results: Word.Range[] = [];

    // Create spanning range from each source to the target.
    // expandTo() handles order automatically - creates range from earlier to later position.
    for (const sourceRange of sourceRanges) {
      const spannedRange = sourceRange.expandTo(targetRange);
      spannedRange.load();
      results.push(spannedRange);
    }

    await context.sync();
    return results;
  }

  /**
   * Get tracked range metadata or throw appropriate error.
   */
  private getTrackedRangeMetadataOrThrow(rangeId: string, label: string): { text: string } {
    const metadata = getTrackedRangeMetadata(rangeId);
    if (metadata) {
      return metadata;
    }

    if (isRangeReleased(rangeId)) {
      throw new QueryError(
        "RANGE_RELEASED",
        `${label} "${rangeId}" was released due to memory management`,
        undefined,
        "Re-query to get a fresh tracked range ID",
      );
    }

    throw new QueryError(
      "NOT_FOUND",
      `${label} "${rangeId}" not found. It may have expired or the document was reloaded.`,
    );
  }

  /**
   * QueryRangeOperation does not merge with other operations.
   */
  merge(_other: Operation<unknown>): Operation<QueryRangeResult> | undefined {
    return undefined;
  }
}

/**
 * Custom error class for query range errors.
 */
class QueryError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly opIndex?: number,
    public readonly fixHint?: string,
  ) {
    super(message);
    this.name = "QueryError";
  }

  toJSON() {
    return {
      code: this.code,
      message: this.message,
      opIndex: this.opIndex,
      fixHint: this.fixHint,
    };
  }
}
